// Binarios que llegan del backend en base64.
//
// El patrón se repite en toda la app: la respuesta trae el fichero embebido en
// base64 (no hay endpoints de descarga aparte) y el navegador necesita un Blob
// para previsualizarlo en un `<iframe>` o dispararlo como descarga.

const MIME_POR_TIPO: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
}

/** MIME a partir de la extensión. Lo desconocido va como binario opaco, que el
 *  navegador ofrece descargar en vez de intentar pintarlo. */
export function getMimeType(tipo: string): string {
  return MIME_POR_TIPO[tipo.toLowerCase()] ?? 'application/octet-stream'
}

/** base64 → Blob. Espera el base64 crudo, SIN el prefijo `data:`. */
export function base64ToBlob(b64: string, mime: string): Blob {
  const chars = atob(b64)
  const bytes = new Uint8Array(chars.length)
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Margen antes de liberar el blob de una descarga.
 *
 *  NO se puede revocar justo después del `click()`, que es lo que parece
 *  correcto y es el error clásico: `revokeObjectURL` mata la URL de inmediato
 *  (verificado en navegador: un `fetch` posterior lanza TypeError al instante).
 *  Si el usuario tiene activado «preguntar dónde guardar cada archivo», el
 *  navegador abre el diálogo ANTES de leer el blob, y al confirmar ya no existe:
 *  la descarga falla con un error de red y el nombre sugerido degenera en el UUID
 *  de la URL. Dos minutos dan margen de sobra para elegir carpeta y acotan la
 *  memoria retenida. */
const MARGEN_REVOKE_MS = 120_000

/** Dispara la descarga de una URL que YA existe, con el nombre dado.
 *
 *  No revoca nada: la URL no es suya. Úsala cuando quien llama ya mantiene el
 *  blob vivo (por ejemplo, el mismo que alimenta una previsualización), porque
 *  así no hay un segundo blob ni carrera posible con el diálogo de guardado.
 *
 *  El `<a>` se añade al DOM antes del clic: Firefox ignora el clic en un
 *  elemento que no está montado. */
export function descargarDesdeUrl(url: string, nombreArchivo: string): void {
  const a = document.createElement('a')
  a.href = url
  // Sin nombre el navegador lo deriva de la URL, y en un blob eso es el UUID.
  a.download = nombreArchivo || 'documento'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** Dispara la descarga de un base64, creando el blob y liberándolo más tarde.
 *
 *  Para cuando quien llama no tiene ya una URL viva. Si la tiene, usa
 *  `descargarDesdeUrl` y evítate el blob duplicado. */
export function descargarBase64(b64: string, nombreArchivo: string, mime: string): void {
  const url = URL.createObjectURL(base64ToBlob(b64, mime))
  descargarDesdeUrl(url, nombreArchivo)
  setTimeout(() => URL.revokeObjectURL(url), MARGEN_REVOKE_MS)
}
